/*
  Pure rate-math shared between the React frontend and the Node backend.

  Single source of truth for "what commission rate does this user see for this
  retailer?". Prior to this module the frontend `getAdjPayoutRateData` was the
  authoritative implementation and the backend Partner API approximated it with
  a simpler version that missed CustomAffiliateRates, user_percentage_override,
  closed-program handling, and the shopmyshelf-source branch. Both layers now
  call into this function with explicit primitives so behavior cannot drift.

  Inputs and outputs are intentionally serializable primitives (no Redux user
  shape, no React-specific icon constants). UI concerns (`rateIcon`, `display`)
  are layered by the frontend wrapper in react/src/Helpers/user_helpers.js.
*/

const _ = require('lodash');

const FIXED_FEE_RATE_TYPES = ['fixed_fee', 'flat_fee'];

const hasWinningFixedFeePayout = merchantDetails =>
  !_.get(merchantDetails, 'isClosedProgram', false) && FIXED_FEE_RATE_TYPES.includes(_.get(merchantDetails, 'rateType'));

const findWinningUserBrandCustomRate = ({ customRates = [], merchantDetails } = {}) => {
  if (!merchantDetails?.domain) return undefined;
  const customRate = _.find(
    customRates,
    rate => merchantDetails?.domain === rate?.brand?.domain || rate?.brand?.altDomains?.includes(merchantDetails?.domain)
  );
  return !hasWinningFixedFeePayout(merchantDetails) && customRate?.rate != null ? customRate : undefined;
};

const doesTakePercentageFromUser = brand => {
  if (!brand) return true;
  if (brand.network_rate) return false;
  if (brand.network_rate_gmv_cut) return false;
  return true;
};

/*
  Build the alert metadata that the frontend uses to render a HIGHER/LOWER rate
  badge. Returns null when no badge applies. `baseRate` is the comparison point
  (brand.commission_rate for shopmyshelf merchants, merchant.fullPayout otherwise).
*/
const buildRateAlert = (type, baseRate, merchantDetails) => {
  const allCustomRateValues = _.get(merchantDetails, 'brand.custom_affiliate_rates', []).map(r => r.rate);
  if (!allCustomRateValues.length) return { rateAlertType: type, rateRangeStr: null };
  const allRates = [...allCustomRateValues, baseRate];
  return { rateAlertType: type, rateRangeStr: `${_.min(allRates)}-${_.max(allRates)}%` };
};

/**
 * Pure user-adjusted rate calculation.
 *
 * @param {Object} args
 * @param {Array}  [args.customRates] - User-level CustomRates: [{ rate, brand: { domain, altDomains } }]
 * @param {Object} args.merchantDetails - { domain, fullPayout, rateType, isClosedProgram, source, raw,
 *                                          user_percentage_override, brand: { custom_affiliate_rates: [...] } }
 * @param {Object} [args.matchingVariant] - { id, catalog_group_ids } — empty {} when no variant match
 * @param {number} [args.user_percentage=82] - The user's revenue share percentage
 * @returns {{ rate: number, rateAlertType: 'higher'|'lower'|null, rateRangeStr: string|null }}
 *
 * Payout priority:
 *   1. Fixed-fee merchant payout
 *   2. User's CustomRate for the brand
 *   3. Variant-level CustomAffiliateRate
 *   4. Catalog-group CustomAffiliateRate (highest of any matching group)
 *   5. Merchant rate (or brand.commission_rate for percentage-based shopmyshelf merchants)
 */
const getAdjPayoutRateData = ({ customRates = [], merchantDetails, matchingVariant = {}, user_percentage = 82 } = {}) => {
  const emptyResult = { rate: 0, rateAlertType: null, rateRangeStr: null };
  if (!merchantDetails) return emptyResult;

  const customRate = findWinningUserBrandCustomRate({ customRates, merchantDetails });

  let customAffiliateRatePayout = null;
  if (!_.isEmpty(matchingVariant)) {
    const merchantCustomAffiliateRates = _.get(merchantDetails, 'brand.custom_affiliate_rates', []);
    let variantCustomAffiliateRate = null;
    let groupCustomAffiliateRates = [];

    merchantCustomAffiliateRates.forEach(customAffiliateRate => {
      if (matchingVariant.id && customAffiliateRate.SpecificVariant_id === matchingVariant.id) {
        variantCustomAffiliateRate = customAffiliateRate;
      } else if ((matchingVariant.catalog_group_ids || []).includes(customAffiliateRate.CatalogGroup_id)) {
        // variants can belong to multiple catalog groups, choose highest group rate if any
        groupCustomAffiliateRates.push(customAffiliateRate);
      }
    });

    // prioritize by specificity
    const groupCustomAffiliateRate = _.maxBy(groupCustomAffiliateRates, 'rate');
    customAffiliateRatePayout = variantCustomAffiliateRate?.rate ?? groupCustomAffiliateRate?.rate;
  }

  let merchantPayout = _.get(merchantDetails, 'fullPayout', 0);
  // Closed programs zero out the merchant payout.
  const isMerchantClosedProgram = _.get(merchantDetails, 'isClosedProgram', false);
  if (isMerchantClosedProgram) merchantPayout = 0;

  const userBrandCustomRate = customRate?.rate;
  const effectiveUserPercentage = merchantDetails?.user_percentage_override ?? user_percentage;
  const applyPercentageAdjustment = (rate, applied_percentage) => (rate * applied_percentage) / 100;
  const fixedFeePayout = hasWinningFixedFeePayout(merchantDetails) ? merchantPayout : null;

  // Direct ShopMy partner branch — merchant.raw carries the brand row, and
  // doesTakePercentageFromUser decides whether we apply the user split or pass
  // 100% through (when ShopMy charges a flat network rate instead).
  if (_.get(merchantDetails, 'source') === 'shopmyshelf') {
    let brand = {};
    try {
      brand = JSON.parse(_.get(merchantDetails, 'raw')) || {};
    } catch {
      brand = {};
    }
    const brandRate = isMerchantClosedProgram ? 0 : brand.commission_rate;
    const priorityPayout = fixedFeePayout ?? userBrandCustomRate ?? customAffiliateRatePayout ?? brandRate;
    const appliesUserPercentage = fixedFeePayout !== null || doesTakePercentageFromUser(brand);
    const finalSMSRate = applyPercentageAdjustment(priorityPayout, appliesUserPercentage ? effectiveUserPercentage : 100);

    let alert = { rateAlertType: null, rateRangeStr: null };
    if (fixedFeePayout === null && !userBrandCustomRate && customAffiliateRatePayout && finalSMSRate) {
      if (customAffiliateRatePayout > brandRate) {
        alert = buildRateAlert('higher', brandRate, merchantDetails);
      } else if (customAffiliateRatePayout < brandRate) {
        alert = buildRateAlert('lower', brandRate, merchantDetails);
      }
    }

    return { rate: finalSMSRate, ...alert };
  }

  const priorityPayout = fixedFeePayout ?? userBrandCustomRate ?? customAffiliateRatePayout ?? merchantPayout;
  const finalRate = applyPercentageAdjustment(priorityPayout, effectiveUserPercentage);

  let alert = { rateAlertType: null, rateRangeStr: null };
  if (fixedFeePayout === null && !userBrandCustomRate && customAffiliateRatePayout && finalRate) {
    if (customAffiliateRatePayout > merchantPayout) {
      alert = buildRateAlert('higher', merchantPayout, merchantDetails);
    } else if (customAffiliateRatePayout < merchantPayout) {
      alert = buildRateAlert('lower', merchantPayout, merchantDetails);
    }
  }

  return { rate: finalRate, ...alert };
};

// The one rounding rule for rates a creator reads: one decimal under 3% (two for dollars),
// whole numbers above. getRate in react/src/Helpers/formatting.js renders the same rule.
const roundCreatorRate = (value, { isPercentage = true } = {}) =>
  parseFloat(value.toFixed(value < 3 ? (isPercentage ? 1 : 2) : 0));

module.exports = { getAdjPayoutRateData, findWinningUserBrandCustomRate, doesTakePercentageFromUser, roundCreatorRate };
